import React from 'react';
import { X, List, Flag, Eye, Clock, Target, Quote, ShieldAlert, MessageCircleQuestion, Radar, ListChecks, Play, CheckCircle2 } from 'lucide-react';
import { accentOf } from './OnsiteGuides';
import { ago } from './useOnsitePlan';

export const DONE = ['answered', 'partial', 'skipped'];
export const DOT = { answered: 'bg-emerald-500', partial: 'bg-amber-500', skipped: 'bg-zinc-500', open: 'bg-zinc-800 border border-zinc-600' };
export const AMPEL = [
  { value: 'green', label: 'Grün', on: 'bg-emerald-600 border-emerald-500 text-white', off: 'border-emerald-500/40 text-emerald-300' },
  { value: 'yellow', label: 'Gelb', on: 'bg-amber-500 border-amber-400 text-white', off: 'border-amber-500/40 text-amber-300' },
  { value: 'red', label: 'Rot', on: 'bg-red-600 border-red-500 text-white', off: 'border-red-500/40 text-red-300' },
];

// Runner steps: briefing → per block either its questions or the block itself (peer-led / Rundgang / Abendessen) → closing
export const hasBriefing = (g) => {
  const b = g?.briefing || {};
  return !!(b.header || b.goals?.length || b.stance?.length || b.scripts?.length || (g?.sections || []).some((s) => s.time || s.task));
};
export const buildSteps = (g) => {
  if (!g) return [];
  const out = hasBriefing(g) ? [{ kind: 'briefing', id: 'briefing', label: 'Start' }] : [];
  (g.sections || []).forEach((s, si) => {
    if ((s.questions || []).length) s.questions.forEach((q, qi) => out.push({ kind: 'question', ...q, si, qi, label: `${si + 1}.${qi + 1}`, section: s }));
    else out.push({ kind: 'block', id: s.id, si, label: (s.title || '').match(/^Block\s*\d+/i)?.[0] || `Block ${si + 1}`, section: s, text: s.title });
  });
  out.push({ kind: 'closing', id: 'closing', label: 'Abschluss' });
  return out;
};
export const hasCheatSheet = (b) => !!(b && (b.vocabulary?.say?.length || b.vocabulary?.avoid?.length || b.vocabulary?.never?.length || b.tricky?.length || b.signals?.length || b.scripts?.length));

export function LeadBadge({ section, owner }) {
  if (!section?.lead) return null;
  const me = owner?.split(' ')[0] || 'Ich';
  const txt = section.lead === 'me' ? `${me} führt` : section.lead === 'peer' ? `${section.lead_name || 'Partner'} führt` : 'gemeinsam';
  const cls = section.lead === 'me' ? 'border-teal-500/50 bg-teal-500/10 text-teal-300' : section.lead === 'peer' ? 'border-zinc-500/50 bg-zinc-500/10 text-zinc-300' : 'border-violet-500/50 bg-violet-500/10 text-violet-300';
  return <span className={`text-[10px] font-mono border rounded px-1.5 py-0.5 ${cls}`} data-testid="runner-lead">{txt}</span>;
}

export function BlockMeta({ section, owner }) {
  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      {section.time && <span className="inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground"><Clock className="w-3 h-3" />{section.time}</span>}
      <LeadBadge section={section} owner={owner} />
    </span>
  );
}

function Bullets({ items, testid }) {
  if (!items?.length) return null;
  return <ul className="space-y-1" data-testid={testid}>{items.map((t, i) => <li key={i} className="text-sm text-foreground/90 flex gap-2"><span className="text-muted-foreground">·</span><span>{t}</span></li>)}</ul>;
}

function Head({ icon: Icon, children }) {
  return <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1.5"><Icon className="w-3 h-3" /> {children}</p>;
}

export function Briefing({ guide, onStart }) {
  const b = guide.briefing || {};
  const a = accentOf(guide);
  const timed = (guide.sections || []).filter((s) => s.time || s.lead || s.task);
  return (
    <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-6" data-testid="runner-briefing">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Briefing · vor dem Termin lesen</p>
        <h1 className="font-heading text-2xl sm:text-3xl leading-snug mt-1">{guide.title}</h1>
        {b.header && <p className="text-sm text-muted-foreground mt-2" data-testid="runner-briefing-header">{b.header}</p>}
      </div>
      {b.goals?.length > 0 && (
        <div className={`rounded-sm border p-3 ${a.border} ${a.bg}`}>
          <Head icon={Target}>Was ich nach Hause bringen muss — am Ende abhaken</Head>
          <Bullets items={b.goals.map((g) => g.text)} testid="runner-briefing-goals" />
        </div>
      )}
      {b.stance?.length > 0 && <div><Head icon={ShieldAlert}>Meine Haltung</Head><Bullets items={b.stance} testid="runner-briefing-stance" /></div>}
      {(b.scripts || []).map((s, i) => (
        <div key={i} className="rounded-sm border border-border/70 p-3" data-testid={`runner-briefing-script-${i}`}>
          <Head icon={Quote}>{s.title || 'Sprechtext'}</Head>
          <div className="space-y-2">{s.lines.map((l, j) => <p key={j} className="text-sm text-foreground/90 italic leading-relaxed select-text">{l}</p>)}</div>
        </div>
      ))}
      {timed.length > 0 && (
        <div>
          <Head icon={Clock}>Der Ablauf</Head>
          <div className="divide-y divide-border/60 border border-border/70 rounded-sm" data-testid="runner-briefing-timeline">
            {(guide.sections || []).map((s, si) => (
              <div key={s.id} className="px-3 py-2 flex gap-3 text-sm">
                <span className="font-mono text-[11px] text-muted-foreground w-[5.5rem] shrink-0 pt-0.5">{s.time || ''}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-medium">{si + 1}. {s.title}</span><LeadBadge section={s} owner={guide.owner_name} />{(s.questions || []).length > 0 && <span className="text-[10px] text-muted-foreground">{s.questions.length} Fragen</span>}</div>
                  {s.task && <p className="text-xs text-muted-foreground mt-0.5">{s.task}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <button onClick={onStart} data-testid="runner-start-btn" className={`w-full h-12 rounded-sm text-white text-sm font-medium inline-flex items-center justify-center gap-2 ${a.solid}`}><Play className="w-4 h-4" /> Los geht's — zum ersten Block</button>
    </main>
  );
}

export function CheatSheet({ briefing: b, onClose }) {
  const v = b.vocabulary || {};
  return (
    <aside className="fixed inset-y-0 right-0 z-40 w-full sm:w-[420px] bg-card border-l border-border overflow-y-auto animate-fade-in-up" data-testid="runner-cheatsheet">
      <div className="sticky top-0 bg-card/95 backdrop-blur-sm border-b border-border px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] flex items-center gap-2">
        <Radar className="w-4 h-4 text-amber-400" /><span className="text-sm font-medium">Spickzettel</span>
        <button onClick={onClose} className="ml-auto text-muted-foreground hover:text-foreground h-10 w-10 inline-flex items-center justify-center" data-testid="runner-cheatsheet-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-4 space-y-5">
        {(v.say?.length || v.avoid?.length || v.never?.length) ? (
          <div className="space-y-2">
            <Head icon={MessageCircleQuestion}>Wortwahl</Head>
            {v.say?.length > 0 && <p className="text-xs"><span className="text-emerald-300 font-medium">Sage: </span><span className="text-foreground/90">{v.say.join(' · ')}</span></p>}
            {v.avoid?.length > 0 && <p className="text-xs"><span className="text-amber-300 font-medium">Sage nicht: </span><span className="text-foreground/80">{v.avoid.join(' · ')}</span></p>}
            {v.never?.length > 0 && <p className="text-xs"><span className="text-red-300 font-medium">Nie: </span><span className="text-foreground/80">{v.never.join(' · ')}</span></p>}
          </div>
        ) : null}
        {b.tricky?.length > 0 && (
          <div>
            <Head icon={ShieldAlert}>Heikle Momente</Head>
            <div className="space-y-2">{b.tricky.map((t, i) => (
              <div key={i} className="border border-border/70 rounded-sm p-2.5" data-testid={`runner-tricky-${i}`}>
                {t.if && <p className="text-[11px] text-muted-foreground">{t.if}</p>}
                <p className="text-sm text-foreground/90 mt-0.5 italic select-text">{t.say}</p>
              </div>))}</div>
          </div>
        )}
        {b.signals?.length > 0 && <div><Head icon={Eye}>Signale lesen</Head><Bullets items={b.signals} /></div>}
        {(b.scripts || []).map((s, i) => (
          <div key={i}><Head icon={Quote}>{s.title || 'Sprechtext'}</Head><div className="space-y-1.5">{s.lines.map((l, j) => <p key={j} className="text-xs text-foreground/85 italic select-text">{l}</p>)}</div></div>
        ))}
      </div>
    </aside>
  );
}

export function CheckItem({ on, text, onToggle, testid }) {
  return (
    <button onClick={onToggle} data-testid={testid}
      className={`w-full text-left flex items-start gap-2 text-sm rounded-sm px-2.5 py-2 border ${on ? 'border-emerald-500/40 bg-emerald-500/10 text-foreground/70' : 'border-border/70 text-foreground hover:bg-secondary/50'}`}>
      <span className={`mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${on ? 'bg-emerald-600 border-emerald-500' : 'border-border'}`}>{on && <CheckCircle2 className="w-3 h-3 text-white" />}</span>
      <span className={on ? 'line-through' : ''}>{text}</span>
    </button>
  );
}

export function Closing({ guide, progress, toggleDone, setAmpel, onNote, notes, steps }) {
  const b = guide.briefing || {};
  const a = accentOf(guide);
  const flagged = steps.filter((s) => (s.kind === 'question' || s.kind === 'block') && progress[s.id]?.followup);
  return (
    <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-6" data-testid="runner-closing">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Abschluss · bevor ich ins Auto steige</p>
        <h1 className="font-heading text-2xl sm:text-3xl leading-snug mt-1">Was habe ich mitgenommen?</h1>
      </div>
      {b.goals?.length > 0 && (
        <div className={`rounded-sm border p-3 ${a.border} ${a.bg}`}>
          <Head icon={Target}>Ziele — abhaken, was ich jetzt weiß</Head>
          <div className="space-y-1.5" data-testid="runner-closing-goals">{b.goals.map((g) => <CheckItem key={g.id} on={progress[g.id]?.status === 'answered'} text={g.text} onToggle={() => toggleDone(g.id)} testid={`runner-goal-${g.id}`} />)}</div>
        </div>
      )}
      {b.hypotheses?.length > 0 && (
        <div>
          <Head icon={Radar}>Hypothesen ampeln</Head>
          <div className="space-y-2" data-testid="runner-closing-hypotheses">
            {b.hypotheses.map((h) => {
              const p = progress[h.id] || {};
              return (
                <div key={h.id} className="border border-border/70 rounded-sm p-2.5 space-y-2" data-testid={`runner-hyp-${h.id}`}>
                  <p className="text-sm"><span className="font-mono text-xs text-muted-foreground mr-1.5">{h.label}</span>{h.text}</p>
                  <div className="flex items-center gap-1.5">
                    {AMPEL.map((o) => <button key={o.value} onClick={() => setAmpel(h.id, p.ampel === o.value ? 'open' : o.value)} data-testid={`runner-hyp-${h.id}-${o.value}`}
                      className={`h-8 px-3 rounded-sm border text-xs font-medium ${p.ampel === o.value ? o.on : `${o.off} bg-transparent`}`}>{o.label}</button>)}
                    <input value={notes[h.id] ?? p.note ?? ''} onChange={(e) => onNote(h.id, e.target.value)} placeholder="Begründung / Zitat" data-testid={`runner-hyp-${h.id}-note`}
                      className="flex-1 min-w-[120px] bg-secondary border border-border rounded-sm h-8 px-2 text-xs text-foreground focus:outline-none select-text" />
                  </div>
                </div>
              );
            })}
          </div>
          {b.hypothesis_rule && <p className="text-xs text-muted-foreground mt-2 italic" data-testid="runner-hyp-rule">Regel: {b.hypothesis_rule}</p>}
        </div>
      )}
      {b.closing?.length > 0 && (
        <div>
          <Head icon={ListChecks}>Checkliste</Head>
          <div className="space-y-1.5" data-testid="runner-closing-checklist">{b.closing.map((c) => <CheckItem key={c.id} on={progress[c.id]?.status === 'answered'} text={c.text} onToggle={() => toggleDone(c.id)} testid={`runner-close-${c.id}`} />)}</div>
        </div>
      )}
      {flagged.length > 0 && (
        <div>
          <Head icon={Flag}>Follow-ups ({flagged.length})</Head>
          <ul className="space-y-1" data-testid="runner-closing-followups">{flagged.map((s) => <li key={s.id} className="text-sm text-foreground/90"><span className="font-mono text-xs text-muted-foreground mr-1.5">{s.label}</span>{s.text}{progress[s.id]?.note ? <span className="text-muted-foreground"> — {progress[s.id].note}</span> : null}</li>)}</ul>
        </div>
      )}
      <div>
        <Head icon={Quote}>Das eine Zitat des Abends · Gesamteindruck</Head>
        <textarea value={notes.closing ?? progress.closing?.note ?? ''} onChange={(e) => onNote('closing', e.target.value)} rows={4} data-testid="runner-closing-note" placeholder="Wortlaut Preisvorstellung, Stimmung, was mir hängen geblieben ist …"
          className="w-full bg-secondary border border-border rounded-sm p-3 text-base sm:text-sm text-foreground focus:outline-none focus:border-foreground/40 resize-y select-text" />
      </div>
      {b.after?.length > 0 && <div><Head icon={List}>Nach dem Termin</Head><Bullets items={b.after} testid="runner-closing-after" /></div>}
      <p className="text-xs text-muted-foreground">Zurück im Planer: „Ergebnisse ins Debrief übernehmen“ und „Protokoll erzeugen“ — alles hier Eingetragene landet darin.</p>
    </main>
  );
}

export function PeerChip({ p, onClick, active }) {
  const a = accentOf(p);
  const c = p.counts || {};
  const done = (c.answered || 0) + (c.partial || 0) + (c.skipped || 0);
  const lbl = p.cursor_info ? (p.cursor_info.label.includes('.') ? `Frage ${p.cursor_info.label}` : p.cursor_info.label) : 'noch nicht gestartet';
  return (
    <button onClick={onClick} data-testid={`runner-peer-${p.id}`}
      className={`inline-flex items-center gap-1.5 text-[11px] border rounded-full pl-1.5 pr-2.5 h-7 ${a.border} ${active ? a.bg : ''} ${a.text}`}>
      <span className={`w-2 h-2 rounded-full ${a.solid}`} />
      <span className="font-medium">{p.owner_name?.split(' ')[0]}</span>
      <span className="text-muted-foreground">· {lbl} · {done}/{c.total}</span>
    </button>
  );
}

export function PeerPanel({ peer, onClose }) {
  const a = accentOf(peer);
  return (
    <aside className="fixed inset-y-0 right-0 z-40 w-full sm:w-[380px] bg-card border-l border-border overflow-y-auto animate-fade-in-up" data-testid="runner-peer-panel">
      <div className="sticky top-0 bg-card/95 backdrop-blur-sm border-b border-border px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] flex items-center gap-2">
        <span className={`w-2.5 h-2.5 rounded-full ${a.solid}`} />
        <span className={`text-sm font-medium ${a.text}`}>{peer.owner_name}</span>
        <span className="text-xs text-muted-foreground truncate">{peer.title}</span>
        <button onClick={onClose} className="ml-auto text-muted-foreground hover:text-foreground h-10 w-10 inline-flex items-center justify-center" data-testid="runner-peer-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-4 space-y-4">
        {peer.cursor_info && (
          <div className={`border rounded-sm p-3 ${a.border} ${a.bg}`}>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Gerade bei</p>
            <p className="text-sm text-foreground mt-1"><span className="font-mono text-xs text-muted-foreground mr-1">{peer.cursor_info.label}</span>{peer.cursor_info.text}</p>
            <p className="text-[10px] text-muted-foreground mt-1">{peer.cursor_info.section} · {ago(peer.cursor_info.at)}</p>
          </div>
        )}
        {(peer.recent_notes || []).length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Notizen von {peer.owner_name?.split(' ')[0]}</p>
            <div className="space-y-2">
              {peer.recent_notes.map((n, i) => (
                <div key={i} className="border border-border/70 rounded-sm p-2">
                  <p className="text-[11px] text-muted-foreground"><span className="font-mono">{n.label}</span> {n.question}{n.followup && <span className="ml-1 text-amber-300">· Follow-up</span>}</p>
                  {n.note && <p className="text-xs text-foreground mt-1 whitespace-pre-wrap">{n.note}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Ablauf & Fragen</p>
          {(peer.sections || []).map((s, si) => {
            const blockSt = peer.progress?.[s.id]?.status || 'open';
            const curBlock = peer.cursor_info?.question_id === s.id;
            return (
              <div key={s.id} className="mb-2">
                <p className="text-[11px] text-foreground/80 font-medium flex items-center gap-1.5">{s.time && <span className="font-mono text-muted-foreground">{s.time}</span>}{si + 1}. {s.title}<LeadBadge section={s} owner={peer.owner_name} /></p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(s.questions || []).length === 0 ? (
                    <span title={s.must_learn || s.title} className={`h-5 px-1.5 rounded-sm text-[9px] font-mono flex items-center ${DOT[blockSt]} ${curBlock ? `ring-2 ${a.ring}` : ''} ${blockSt === 'open' ? 'text-muted-foreground' : 'text-white'}`}>Block</span>
                  ) : s.questions.map((q, qi) => {
                    const st = peer.progress?.[q.id]?.status || 'open';
                    const cur = peer.cursor_info?.question_id === q.id;
                    return <span key={q.id} title={q.text} className={`w-5 h-5 rounded-sm text-[9px] font-mono flex items-center justify-center ${DOT[st]} ${cur ? `ring-2 ${a.ring}` : ''} ${st === 'open' ? 'text-muted-foreground' : 'text-white'}`}>{qi + 1}</span>;
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-muted-foreground">{peer.counts?.total} Schritte · aktualisiert {ago(peer.updated_at)}</p>
      </div>
    </aside>
  );
}

export function Overview({ guide, steps, idx, onJump, onClose, peers }) {
  const a = accentOf(guide);
  const peerAt = (id) => peers.filter((p) => p.cursor_info?.question_id && buildSteps(p).find((x) => x.id === p.cursor_info.question_id)?.label === steps.find((s) => s.id === id)?.label);
  const Row = ({ step, i, indent }) => {
    const st = guide.progress?.[step.id]?.status || 'open';
    return (
      <button onClick={() => { onJump(i); onClose(); }} data-testid={`runner-jump-${step.id}`}
        className={`w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-sm hover:bg-secondary/60 ${i === idx ? `${a.bg} ring-1 ${a.ring}` : ''} ${indent ? 'ml-3' : ''}`}>
        <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${step.kind === 'question' || step.kind === 'block' ? DOT[st] : 'bg-secondary'}`} />
        <span className="text-[10px] font-mono text-muted-foreground mt-0.5 shrink-0 w-8">{step.kind === 'question' ? step.label : ''}</span>
        <span className="text-xs text-foreground/90 flex-1">{step.kind === 'block' ? (step.section.must_learn || step.section.task || step.section.title) : step.kind === 'briefing' ? 'Briefing lesen' : step.kind === 'closing' ? 'Ziele · Hypothesen · Checkliste' : step.text}</span>
        {guide.progress?.[step.id]?.followup && <Flag className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />}
        {peerAt(step.id).map((p) => <span key={p.id} title={`${p.owner_name} ist hier`} className={`w-2 h-2 rounded-full mt-1 ${accentOf(p).solid}`} />)}
      </button>
    );
  };
  return (
    <aside className="fixed inset-y-0 left-0 z-40 w-full sm:w-[400px] bg-card border-r border-border overflow-y-auto" data-testid="runner-overview">
      <div className="sticky top-0 bg-card/95 backdrop-blur-sm border-b border-border px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] flex items-center gap-2">
        <List className="w-4 h-4 text-muted-foreground" /><span className="text-sm font-medium">Übersicht</span>
        <button onClick={onClose} className="ml-auto text-muted-foreground hover:text-foreground h-10 w-10 inline-flex items-center justify-center" data-testid="runner-overview-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-3 space-y-3">
        {steps[0]?.kind === 'briefing' && <Row step={steps[0]} i={0} />}
        {(guide.sections || []).map((s, si) => (
          <div key={s.id}>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground px-1 mb-1 flex items-center gap-2 flex-wrap">{s.time && <span className="font-mono">{s.time}</span>}{si + 1}. {s.title}<LeadBadge section={s} owner={guide.owner_name} /></p>
            {steps.map((st, i) => (st.si === si ? <Row key={st.id} step={st} i={i} indent={st.kind === 'question'} /> : null))}
          </div>
        ))}
        <Row step={steps[steps.length - 1]} i={steps.length - 1} />
      </div>
    </aside>
  );
}
